import React from 'react'
import { X } from 'lucide-react'

const ViewEmployeeAllowanceModal = ({ isOpen, onClose, allowance }) => {
  if (!isOpen || !allowance) return null

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">View Employee Allowances</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Employee Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Employee Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Full Name</label>
                  <p className="text-gray-900">{allowance.employee?.fullName || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Job Title</label>
                  <p className="text-gray-900">{allowance.employee?.jobTitle || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Branch</label>
                  <p className="text-gray-900">{allowance.employee?.branchName || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Employee Type</label>
                  <p className="text-gray-900 capitalize">{allowance.employee?.employeeType || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Allowance Details */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Allowance Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* House Allowance */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-2">House Allowance</label>
                  <p className="text-2xl font-semibold text-gray-900">
                    Rs. {formatCurrency(allowance.houseAllowance)}
                  </p>
                </div>

                {/* Transport Allowance */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-2">Transport Allowance</label>
                  <p className="text-2xl font-semibold text-gray-900">
                    Rs. {formatCurrency(allowance.transportAllowance)}
                  </p>
                </div>

                {/* Food Allowance */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-2">Food Allowance</label>
                  <p className="text-2xl font-semibold text-gray-900">
                    Rs. {formatCurrency(allowance.foodAllowance)}
                  </p>
                </div>

                {/* Health Allowance */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-2">Health Allowance</label>
                  <p className="text-2xl font-semibold text-gray-900">
                    Rs. {formatCurrency(allowance.healthAllowance)}
                  </p>
                </div>
              </div>

              {/* Total Allowances */}
              <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-orange-800 mb-2">Total Monthly Allowances</label>
                <p className="text-3xl font-bold text-orange-900">
                  Rs. {formatCurrency(
                    (allowance.houseAllowance || 0) + 
                    (allowance.transportAllowance || 0) + 
                    (allowance.foodAllowance || 0) + 
                    (allowance.healthAllowance || 0)
                  )}
                </p>
              </div>
            </div>

            {/* System Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">System Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Created By</label>
                  <p className="text-gray-900">{allowance.createdBy?.username || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Created At</label>
                  <p className="text-gray-900">{formatDate(allowance.createdAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Last Updated</label>
                  <p className="text-gray-900">{formatDate(allowance.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="flex justify-end p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewEmployeeAllowanceModal
